import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import storeService from '@/api/services/storeService';
import { getErrorMessage } from '@/utils/errorUtils';

type VerifyState = 'idle' | 'verifying' | 'success' | 'error';

export default function CheckoutVerify() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyState>('idle');
  const [message, setMessage] = useState('');

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    const txRefParam = searchParams.get('tx_ref') || searchParams.get('txRef');
    const transactionIdParam =
      searchParams.get('transaction_id') || searchParams.get('transactionId');

    const txRef = txRefParam || localStorage.getItem('pendingCheckoutTxRef');

    if (!transactionIdParam || !txRef) {
      setState('error');
      setMessage('Missing payment verification parameters.');
      return;
    }

    setState('verifying');
    storeService
      .verifyCheckoutPayment({
        transactionId: String(transactionIdParam),
        txRef: String(txRef),
      })
      .then((res) => {
        if (res?.success) {
          setState('success');
          setMessage(res?.message || 'Payment verified successfully.');
          localStorage.removeItem('pendingCheckoutTxRef');
          localStorage.removeItem('pendingCheckoutOrderId');
          const target = '/operator/orders';
          setTimeout(() => {
            if (window.top && window.top !== window) {
              window.top.location.assign(target);
            } else {
              navigate(target);
            }
          }, 1500);
          return;
        }
        setState('error');
        setMessage(res?.message || 'Verification failed.');
      })
      .catch((err: unknown) => {
        setState('error');
        setMessage(getErrorMessage(err) || String(err) || 'Verification failed.');
      });
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white dark:bg-[#2a2b2e] rounded-2xl shadow p-6 border border-gray-200 dark:border-gray-800 text-center">
        {state === 'verifying' || state === 'idle' ? (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Verifying Payment
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Please wait while we confirm your transaction.
            </p>
          </>
        ) : state === 'success' ? (
          <>
            <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
              &#10003;
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Payment Successful
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {message}
            </p>
            <p className="text-xs text-gray-400 mt-3">
              Redirecting to your orders…
            </p>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-3">
              &#215;
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Payment Failed
            </h1>
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              {message}
            </p>
            <button
              onClick={() => navigate('/operator/checkout')}
              className="mt-5 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 transition-colors"
            >
              Return to Checkout
            </button>
          </>
        )}
      </div>
    </div>
  );
}
